/**
 * __FLEET_TRACKING__ (2026-10-03) pure GPS-track maths for the Fleet Tracking view.
 *
 * Henry: "live view all GPS tracked activity ... a timeline view so I can slide the daily
 * time bar to any time of day and see the entire fleet at any moment ... strong history view".
 *
 * Everything here is pure (no imports) so sim/fleettrack.test.mjs evaluates this file as-is.
 * A "track" is one driver's pings for a period, normalised by normalizePings():
 *   [{ t (ms UTC), lat, lng, spd (km/h|null), hdg (deg|null), acc (m|null) }]  sorted by t.
 *
 * Time: gps_locations.timestamp is NAIVE UTC ("2026-10-02T18:11:18.89"); a zone-less string is
 * read as UTC here (same rule as utils/dbTime.parseDbTime and utils/offShift.tsMs).
 */

const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;

/** ms since epoch of a DB time; zone-less = UTC. null if unparsable. */
export const tsMs = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) { const v = value.getTime(); return Number.isFinite(v) ? v : null; }
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  let s = String(value).trim();
  if (!HAS_OFFSET.test(s)) s = s.replace(' ', 'T').replace(/(\.\d{3})\d+$/, '$1') + 'Z';
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : null;
};

/** A ms time the way gps_locations.timestamp stores it (naive UTC). */
export const naiveUtc = (ms) => new Date(ms).toISOString().replace('Z', '');

/** Local calendar day 'YYYY-MM-DD' -> { startMs, endMs } (local midnight to next local midnight; DST-safe). */
export const dayBounds = (ymd) => {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]), mo = Number(m[2]) - 1, d = Number(m[3]);
  return { startMs: new Date(y, mo, d, 0, 0, 0, 0).getTime(), endMs: new Date(y, mo, d + 1, 0, 0, 0, 0).getTime() };
};

/** A Date/ms as the local 'YYYY-MM-DD'. */
export const localYmd = (ms = Date.now()) => {
  const d = new Date(ms);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
};

const num = (v) => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Raw gps_locations rows -> a clean, time-sorted track. Drops rows without a usable position
 * (null / 0,0 / out of range) and fixes worse than maxAccuracyM; keeps one row per timestamp.
 */
export const normalizePings = (rows, { maxAccuracyM = 250 } = {}) => {
  const out = [];
  (rows || []).forEach((r) => {
    if (!r) return;
    const t = tsMs(r.timestamp != null ? r.timestamp : r.t);
    const lat = num(r.latitude != null ? r.latitude : r.lat);
    const lng = num(r.longitude != null ? r.longitude : r.lng);
    if (t == null || lat == null || lng == null) return;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) return;
    const acc = num(r.accuracy_meters != null ? r.accuracy_meters : r.acc);
    if (acc != null && maxAccuracyM != null && acc > maxAccuracyM) return;
    out.push({ t, lat, lng, spd: num(r.speed_kmh != null ? r.speed_kmh : r.spd), hdg: num(r.heading != null ? r.heading : r.hdg), acc });
  });
  out.sort((a, b) => a.t - b.t);
  const dedup = [];
  out.forEach((p) => { if (!dedup.length || dedup[dedup.length - 1].t !== p.t) dedup.push(p); });
  return dedup;
};

/** Merge newer pings into a track (live polling); keeps order and one row per timestamp. */
export const mergeTrack = (track, more) => {
  if (!more || !more.length) return track || [];
  const byT = new Map();
  (track || []).forEach((p) => byT.set(p.t, p));
  more.forEach((p) => byT.set(p.t, p));
  return Array.from(byT.values()).sort((a, b) => a.t - b.t);
};

/** Great-circle distance in metres. */
export const haversineM = (a, b) => {
  const R = 6371000;
  const toR = Math.PI / 180;
  const dLat = (b.lat - a.lat) * toR;
  const dLng = (b.lng - a.lng) * toR;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * toR) * Math.cos(b.lat * toR) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

/** Initial bearing a -> b in degrees (0 = north, clockwise). */
export const bearingDeg = (a, b) => {
  const toR = Math.PI / 180;
  const y = Math.sin((b.lng - a.lng) * toR) * Math.cos(b.lat * toR);
  const x = Math.cos(a.lat * toR) * Math.sin(b.lat * toR) - Math.sin(a.lat * toR) * Math.cos(b.lat * toR) * Math.cos((b.lng - a.lng) * toR);
  return (Math.atan2(y, x) / toR + 360) % 360;
};

/** Index of the last point with p.t <= t (-1 if none). Binary search. */
export const indexAtOrBefore = (track, t) => {
  let lo = 0, hi = (track || []).length - 1, ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (track[mid].t <= t) { ans = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return ans;
};

const lerpAngle = (a, b, f) => {
  const d = ((((b - a) % 360) + 540) % 360) - 180;
  return (a + d * f + 360) % 360;
};

/**
 * Where the truck was at time t.
 *  - before the first ping -> null (no position yet that day)
 *  - between two pings closer than maxGapMs -> linear interpolation (position, speed, heading)
 *  - across a gap > maxGapMs -> held at the earlier ping, flagged gap:true
 *  - after the last ping -> held at the last ping, flagged after:true with ageMs
 * Returns { lat, lng, spd, hdg, t, ageMs, interpolated, gap, after }.
 */
export const positionAt = (track, t, { maxGapMs = 5 * 60000 } = {}) => {
  if (!track || !track.length) return null;
  const i = indexAtOrBefore(track, t);
  if (i < 0) return null;
  const a = track[i];
  if (i === track.length - 1 || a.t === t) {
    return { lat: a.lat, lng: a.lng, spd: a.spd, hdg: a.hdg, t: a.t, ageMs: t - a.t, interpolated: false, gap: false, after: i === track.length - 1 && t > a.t };
  }
  const b = track[i + 1];
  const span = b.t - a.t;
  if (span > maxGapMs) {
    return { lat: a.lat, lng: a.lng, spd: a.spd, hdg: a.hdg, t: a.t, ageMs: t - a.t, interpolated: false, gap: true, after: false };
  }
  const f = span > 0 ? (t - a.t) / span : 0;
  const moved = haversineM(a, b);
  let hdg;
  if (moved > 15) hdg = bearingDeg(a, b);
  else if (a.hdg != null && b.hdg != null) hdg = lerpAngle(a.hdg, b.hdg, f);
  else hdg = a.hdg != null ? a.hdg : b.hdg;
  const spd = a.spd != null && b.spd != null ? a.spd + (b.spd - a.spd) * f : (a.spd != null ? a.spd : b.spd);
  return { lat: a.lat + (b.lat - a.lat) * f, lng: a.lng + (b.lng - a.lng) * f, spd, hdg, t, ageMs: 0, interpolated: true, gap: false, after: false };
};

/** The points from (t - minutes) up to t, ending with the interpolated position at t. */
export const trailWindow = (track, t, minutes, opts) => {
  if (!track || !track.length || !(minutes > 0)) return [];
  const from = t - minutes * 60000;
  const lo = Math.max(0, indexAtOrBefore(track, from) + 1);
  const hi = indexAtOrBefore(track, t);
  const pts = hi >= lo ? track.slice(lo, hi + 1) : [];
  const here = positionAt(track, t, opts);
  if (here && here.interpolated) pts.push({ t, lat: here.lat, lng: here.lng, spd: here.spd, hdg: here.hdg });
  return pts;
};

/** Breaks in reporting longer than gapMin: [{ startMs, endMs, durMin, from, to, distM }]. */
export const detectGaps = (track, gapMin = 5) => {
  const out = [];
  const lim = gapMin * 60000;
  for (let i = 1; i < (track || []).length; i++) {
    const a = track[i - 1], b = track[i];
    if (b.t - a.t > lim) out.push({ startMs: a.t, endMs: b.t, durMin: Math.round((b.t - a.t) / 60000), from: a, to: b, distM: Math.round(haversineM(a, b)) });
  }
  return out;
};

/**
 * Stops / dwells: runs of pings that stay within radiusM of their running centre for at
 * least minDwellMin. A reporting gap inside the run still counts (parked overnight with the
 * phone asleep is a stop). Returns [{ arriveMs, departMs, durMin, lat, lng, n, startIdx, endIdx, ongoing }].
 */
export const detectStops = (track, { minDwellMin = 5, radiusM = 120 } = {}) => {
  const out = [];
  const n = (track || []).length;
  const minMs = minDwellMin * 60000;
  let i = 0;
  while (i < n) {
    let cLat = track[i].lat, cLng = track[i].lng, k = 1;
    let j = i;
    while (j + 1 < n && haversineM({ lat: cLat, lng: cLng }, track[j + 1]) <= radiusM) {
      j++; k++;
      cLat += (track[j].lat - cLat) / k;
      cLng += (track[j].lng - cLng) / k;
    }
    const dur = track[j].t - track[i].t;
    if (j > i && dur >= minMs) {
      out.push({ arriveMs: track[i].t, departMs: track[j].t, durMin: Math.round(dur / 60000), lat: cLat, lng: cLng, n: j - i + 1, startIdx: i, endIdx: j, ongoing: j === n - 1 });
      i = j + 1;
    } else {
      i++;
    }
  }
  return out;
};

/**
 * Daily summary. Distance skips jitter inside detected stops and does not count straight-line
 * jumps across reporting gaps (those are reported separately as gapDistKm).
 */
export const daySummary = (track, { stops = null, gapMin = 5, movingKmh = 5, minDwellMin = 5, radiusM = 120 } = {}) => {
  const t = track || [];
  const st = stops || detectStops(t, { minDwellMin, radiusM });
  const inStop = new Int32Array(t.length).fill(-1);
  st.forEach((s, si) => { for (let k = s.startIdx; k <= s.endIdx; k++) inStop[k] = si; });
  const gapMs = gapMin * 60000;
  let distM = 0, gapDistM = 0, driveMs = 0, gapTotalMs = 0, maxSpd = 0;
  for (let i = 1; i < t.length; i++) {
    const a = t[i - 1], b = t[i];
    const dt = b.t - a.t;
    const d = haversineM(a, b);
    if (dt > gapMs) { gapTotalMs += dt; gapDistM += d; continue; }
    if (inStop[i] !== -1 && inStop[i] === inStop[i - 1]) continue;
    distM += d;
    const kmh = dt > 0 ? (d / 1000) / (dt / 3600000) : 0;
    const spd = b.spd != null ? b.spd : kmh;
    if (spd > movingKmh || kmh > movingKmh) driveMs += dt;
  }
  t.forEach((p) => { if (p.spd != null && p.spd > maxSpd) maxSpd = p.spd; });
  const dwellMs = st.reduce((s, x) => s + (x.departMs - x.arriveMs), 0);
  const driveH = driveMs / 3600000;
  return {
    pings: t.length,
    firstMs: t.length ? t[0].t : null,
    lastMs: t.length ? t[t.length - 1].t : null,
    distanceKm: Math.round(distM / 100) / 10,
    gapDistKm: Math.round(gapDistM / 100) / 10,
    driveMin: Math.round(driveMs / 60000),
    dwellMin: Math.round(dwellMs / 60000),
    gapMin: Math.round(gapTotalMs / 60000),
    stops: st.length,
    maxSpeedKmh: Math.round(maxSpd),
    avgMovingKmh: driveH > 0 ? Math.round((distM / 1000) / driveH) : 0,
  };
};

/** Runs above limitKmh lasting >= minSec: [{ startMs, endMs, durSec, maxKmh, lat, lng }]. */
export const speedingEvents = (track, limitKmh, { minSec = 20, gapMin = 5 } = {}) => {
  const out = [];
  if (!(limitKmh > 0)) return out;
  let run = null;
  const close = () => {
    if (run && (run.endMs - run.startMs) / 1000 >= minSec) out.push({ ...run, durSec: Math.round((run.endMs - run.startMs) / 1000) });
    run = null;
  };
  (track || []).forEach((p, i) => {
    const over = p.spd != null && p.spd > limitKmh;
    const broken = run && i > 0 && p.t - track[i - 1].t > gapMin * 60000;
    if (broken) close();
    if (over) {
      if (!run) run = { startMs: p.t, endMs: p.t, maxKmh: p.spd, lat: p.lat, lng: p.lng };
      else { run.endMs = p.t; if (p.spd > run.maxKmh) { run.maxKmh = p.spd; run.lat = p.lat; run.lng = p.lng; } }
    } else close();
  });
  close();
  out.forEach((e) => { e.maxKmh = Math.round(e.maxKmh); });
  return out;
};

/** Speed band colour (km/h). */
export const SPEED_BANDS = [
  { max: 5, color: '#8b949e', label: '< 5' },
  { max: 30, color: '#58a6ff', label: '5–30' },
  { max: 60, color: '#3fb950', label: '30–60' },
  { max: 85, color: '#d29922', label: '60–85' },
  { max: 100, color: '#f0883e', label: '85–100' },
  { max: Infinity, color: '#f85149', label: '100+' },
];
export const speedColor = (kmh) => {
  const v = kmh == null ? 0 : kmh;
  for (let i = 0; i < SPEED_BANDS.length; i++) if (v < SPEED_BANDS[i].max) return SPEED_BANDS[i].color;
  return SPEED_BANDS[SPEED_BANDS.length - 1].color;
};

/**
 * Track -> polyline pieces of one colour each, split at reporting gaps:
 * [{ color, path: [{lat,lng}], gap:false }] plus [{ gap:true, path:[from,to] }] for each gap.
 */
export const speedSegments = (track, { gapMin = 5 } = {}) => {
  const out = [];
  const t = track || [];
  let cur = null;
  for (let i = 0; i < t.length; i++) {
    const p = t[i];
    if (i > 0 && p.t - t[i - 1].t > gapMin * 60000) {
      out.push({ gap: true, path: [{ lat: t[i - 1].lat, lng: t[i - 1].lng }, { lat: p.lat, lng: p.lng }] });
      cur = null;
    }
    const c = speedColor(p.spd);
    if (!cur || cur.color !== c) {
      const start = cur ? [cur.path[cur.path.length - 1]] : [];
      cur = { color: c, gap: false, path: start.concat([{ lat: p.lat, lng: p.lng }]) };
      out.push(cur);
    } else cur.path.push({ lat: p.lat, lng: p.lng });
  }
  return out.filter((s) => s.path.length >= 2 || s.gap);
};

/**
 * Live state of a driver from his newest ping:
 * 'moving' | 'idle' | 'stale' (no ping for staleMin) | 'offline' (no ping for offlineMin) | 'nodata'.
 */
export const liveState = (last, nowMs, { staleMin = 10, offlineMin = 30, movingKmh = 5 } = {}) => {
  if (!last) return 'nodata';
  const age = (nowMs - last.t) / 60000;
  if (age > offlineMin) return 'offline';
  if (age > staleMin) return 'stale';
  return last.spd != null && last.spd > movingKmh ? 'moving' : 'idle';
};

export const STATE_META = {
  moving: { label: 'Moving', color: '#3fb950' },
  idle: { label: 'Stopped', color: '#d29922' },
  stale: { label: 'Stale', color: '#f0883e' },
  offline: { label: 'Offline', color: '#f85149' },
  nodata: { label: 'No data', color: '#6e7681' },
  offshift: { label: 'Off shift', color: '#8b949e' },
};

/** Activity lanes for the fleet timeline: [{ kind:'move'|'stop'|'gap', startMs, endMs }]. */
export const activityBlocks = (track, { stops = null, gapMin = 5, minDwellMin = 5, radiusM = 120 } = {}) => {
  const t = track || [];
  if (!t.length) return [];
  const st = stops || detectStops(t, { minDwellMin, radiusM });
  const gaps = detectGaps(t, gapMin);
  const marks = [];
  st.forEach((s) => marks.push({ kind: 'stop', startMs: s.arriveMs, endMs: s.departMs }));
  gaps.forEach((g) => {
    // a gap fully inside a stop stays part of the stop (parked, phone asleep)
    const inside = st.some((s) => g.startMs >= s.arriveMs && g.endMs <= s.departMs);
    if (!inside) marks.push({ kind: 'gap', startMs: g.startMs, endMs: g.endMs });
  });
  marks.sort((a, b) => a.startMs - b.startMs);
  const out = [];
  let cursor = t[0].t;
  marks.forEach((m) => {
    if (m.startMs > cursor) out.push({ kind: 'move', startMs: cursor, endMs: m.startMs });
    const s = Math.max(m.startMs, cursor);
    if (m.endMs > s) out.push({ kind: m.kind, startMs: s, endMs: m.endMs });
    cursor = Math.max(cursor, m.endMs);
  });
  if (t[t.length - 1].t > cursor) out.push({ kind: 'move', startMs: cursor, endMs: t[t.length - 1].t });
  return out;
};

const stopArrival = (s) => tsMs(s.actual_arrival_time) != null ? tsMs(s.actual_arrival_time) : tsMs(s.actual_arrival);
const stopDeparture = (s) => tsMs(s.actual_departure_time) != null ? tsMs(s.actual_departure_time) : tsMs(s.actual_departure);
const stopPlanned = (s) => tsMs(s.planned_arrival_at) != null ? tsMs(s.planned_arrival_at)
  : (tsMs(s.estimated_arrival_time) != null ? tsMs(s.estimated_arrival_time) : tsMs(s.estimated_arrival));

/**
 * Jobs vs GPS: for each load stop, the plan, what the system recorded (geofence / driver tap)
 * and what the GPS shows (the detected dwell within radiusM, else the first ping that came
 * within radiusM). Deltas in minutes, positive = late.
 */
export const compareStopsToGps = (loadStops, track, { stops = null, radiusM = 300, minDwellMin = 3 } = {}) => {
  const t = track || [];
  const st = stops || detectStops(t, { minDwellMin, radiusM: 120 });
  return (loadStops || []).map((s) => {
    const lat = num(s.latitude), lng = num(s.longitude);
    const plannedMs = stopPlanned(s);
    const plannedDepMs = tsMs(s.planned_departure_at);
    const recArrMs = stopArrival(s);
    const recDepMs = stopDeparture(s);
    let gpsArrMs = null, gpsDepMs = null, nearestM = null;
    if (lat != null && lng != null && t.length) {
      const here = { lat, lng };
      const cands = st.filter((x) => haversineM(here, x) <= radiusM);
      if (cands.length) {
        const ref = recArrMs != null ? recArrMs : plannedMs;
        cands.sort((a, b) => (ref == null ? a.arriveMs - b.arriveMs : Math.abs(a.arriveMs - ref) - Math.abs(b.arriveMs - ref)));
        gpsArrMs = cands[0].arriveMs; gpsDepMs = cands[0].departMs;
      } else {
        let first = -1, last = -1;
        for (let i = 0; i < t.length; i++) {
          const d = haversineM(here, t[i]);
          if (nearestM == null || d < nearestM) nearestM = d;
          if (d <= radiusM) { if (first < 0) first = i; last = i; } else if (first >= 0) break;
        }
        if (first >= 0) { gpsArrMs = t[first].t; gpsDepMs = t[last].t; }
      }
    }
    const lateMin = (a, p) => (a != null && p != null ? Math.round((a - p) / 60000) : null);
    return {
      id: s.id, load_id: s.load_id, stop_type: s.stop_type, stop_sequence: s.stop_sequence,
      label: s.place_name || s.address || '', address: s.address || '',
      plannedMs, plannedDepMs, recArrMs, recDepMs, gpsArrMs, gpsDepMs,
      nearestM: nearestM != null ? Math.round(nearestM) : null,
      lateVsPlanMin: lateMin(gpsArrMs != null ? gpsArrMs : recArrMs, plannedMs),
      recVsGpsMin: lateMin(recArrMs, gpsArrMs),
      gpsDwellMin: gpsArrMs != null && gpsDepMs != null ? Math.round((gpsDepMs - gpsArrMs) / 60000) : null,
      visited: gpsArrMs != null,
    };
  });
};

/** Coverage grid for the heat layer: [{ lat, lng, n }] cells of cellDeg degrees. */
export const coverageCells = (tracks, cellDeg = 0.01) => {
  const m = new Map();
  (tracks || []).forEach((tr) => (tr || []).forEach((p) => {
    const gy = Math.floor(p.lat / cellDeg), gx = Math.floor(p.lng / cellDeg);
    const k = gy + ':' + gx;
    const c = m.get(k);
    if (c) c.n++; else m.set(k, { lat: gy * cellDeg, lng: gx * cellDeg, n: 1 });
  }));
  return Array.from(m.values());
};

/** Total length of a measured path in km. */
export const pathKm = (pts) => {
  let d = 0;
  for (let i = 1; i < (pts || []).length; i++) d += haversineM(pts[i - 1], pts[i]);
  return Math.round(d / 10) / 100;
};

const csvCell = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

/** Day trail as CSV (local + UTC time columns). */
export const trackToCsv = (track, { driver = '', truck = '' } = {}) => {
  const head = ['driver', 'truck', 'time_local', 'time_utc', 'latitude', 'longitude', 'speed_kmh', 'heading', 'accuracy_m'];
  const rows = (track || []).map((p) => [driver, truck, new Date(p.t).toLocaleString(), new Date(p.t).toISOString(),
    p.lat.toFixed(6), p.lng.toFixed(6), p.spd == null ? '' : Math.round(p.spd * 10) / 10, p.hdg == null ? '' : Math.round(p.hdg), p.acc == null ? '' : Math.round(p.acc)]);
  return [head].concat(rows).map((r) => r.map(csvCell).join(',')).join('\n');
};

const xmlEsc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Day trail as GPX 1.1 (one track, one segment per gap-free run, stops as waypoints). */
export const trackToGpx = (track, { name = 'HMGC trail', gapMin = 5, stops = [] } = {}) => {
  const segs = [];
  let cur = [];
  (track || []).forEach((p, i) => {
    if (i > 0 && p.t - track[i - 1].t > gapMin * 60000) { segs.push(cur); cur = []; }
    cur.push(p);
  });
  if (cur.length) segs.push(cur);
  const wpts = (stops || []).map((s, i) => `  <wpt lat="${s.lat.toFixed(6)}" lon="${s.lng.toFixed(6)}"><time>${new Date(s.arriveMs).toISOString()}</time><name>${xmlEsc('Stop ' + (i + 1) + ' (' + s.durMin + ' min)')}</name></wpt>`).join('\n');
  const body = segs.map((sg) => '    <trkseg>\n' + sg.map((p) =>
    `      <trkpt lat="${p.lat.toFixed(6)}" lon="${p.lng.toFixed(6)}"><time>${new Date(p.t).toISOString()}</time>${p.spd != null ? `<extensions><speed>${(p.spd / 3.6).toFixed(2)}</speed></extensions>` : ''}</trkpt>`).join('\n') + '\n    </trkseg>').join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" creator="HMGC Fleet Tracking" xmlns="http://www.topografix.com/GPX/1/1">\n  <metadata><name>${xmlEsc(name)}</name></metadata>\n${wpts}${wpts ? '\n' : ''}  <trk><name>${xmlEsc(name)}</name>\n${body}\n  </trk>\n</gpx>\n`;
};

/** "1h 05m" / "42m". */
export const fmtDur = (min) => {
  if (min == null || !Number.isFinite(min)) return '—';
  const m = Math.max(0, Math.round(min));
  return m >= 60 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`;
};

/** "3:07 PM" (local). */
export const fmtClock = (ms, withSeconds = false) => {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const d = new Date(ms);
  const h = d.getHours(), mi = d.getMinutes(), s = d.getSeconds();
  return `${h % 12 || 12}:${String(mi).padStart(2, '0')}${withSeconds ? ':' + String(s).padStart(2, '0') : ''} ${h < 12 ? 'AM' : 'PM'}`;
};

/** "just now" / "4 min ago" / "3 h ago" / "2 d ago". */
export const fmtAge = (ms) => {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 48 * 60) return `${Math.round(m / 60)} h ago`;
  return `${Math.round(m / 1440)} d ago`;
};
